<template>
  <section class="main">
    <h1 class="intro">{{ $t("CONFIGURE_FEATURES") }}</h1>
    <div class="row">
      <loader v-if="!inited" />
      <form class="features" v-if="inited">
        <h2 class="heading">
          <html-with-links
            tag="span"
            :html="
              $t('APPDBS_RICKPACTOR', {
                rickpactorLink: '/app/standalone/1900000033',
              })
            "
          ></html-with-links>
          <template
            v-if="config.is_pro === 'no' || config.is_pro_disabled === 'yes'"
          >
            (<template
              v-if="
                details.free_appleid &&
                details.free_appleid_password &&
                !details.free_last_error
              "
              >{{
                $t("RICKPACTOR_CONFIGURED_WITH_APPLE_ID", {
                  appleID: details.free_appleid,
                })
              }}</template
            >
            <template v-else>{{ $t("RICKPACTOR_IS_NOT_CONFIGURED") }}</template
            >)
          </template>
          <p
            class="warning"
            v-if="
              config.model.match(/mac/i) &&
              (details.is_apple_silicon !== 'yes' ||
                parseFloat(details.ios_version) < 11.3)
            "
          >
            {{ $t("RICKPACTOR_SILICON_MAC_VERSION_WARNING") }}
          </p>
        </h2>
        <p
          class="warning"
          v-if="details.is_pro === 'yes' && config.is_pro_disabled === 'no'"
        >
          {{ $t("PRO_ENABLED_NO_RICKPACTOR_NOTICE") }}
        </p>
        <p class="warning" v-else-if="details.ios_version === '13.3.1'">
          {{ $t("ERROR_FREE_DEVELOPER_UNSUPPORTED_IOS_VERSION") }}
        </p>
        <template
          v-else-if="
            (!details.free_appleid || !details.free_appleid_password) &&
            !(
              details.is_apple_silicon === 'yes' &&
              parseFloat(details.ios_version) < 11.3
            )
          "
        >
          <p class="warning" v-html="$t('RICKPACTOR_CONFIGURATION_NOTICE')"></p>
          <div class="feature">
            <p class="feature__text g-text">Apple ID</p>
            <input
              v-model="config.free_appleid"
              placeholder="Enter Apple ID"
              class="feature__input"
              type="text"
            />
          </div>
          <div class="feature">
            <p class="feature__text g-text">{{ $t("PASSWORD") }}</p>
            <input
              v-model="config.free_appleid_password"
              placeholder="Enter password"
              class="feature__input"
              type="password"
            />
          </div>
        </template>
        <template v-else-if="details.free_last_error">
          <p class="warning">
            {{ $t(details.free_last_error.translated) }}
          </p>
          <div class="buttons buttons_sa">
            <button
              class="buttons__delete"
              @click.prevent="reset('reset_free_error')"
            >
              <loader v-if="pending" size="15px" border="3px" absolute />
              {{ $t("RESET_RICKPACTOR_ERROR") }}
            </button>
            <button
              class="buttons__delete"
              @click.prevent="reset('clear_free_developer_entity')"
            >
              <loader v-if="pending" size="15px" border="3px" absolute />
              {{ $t("CLEAR_RICKPACTOR_CONFIGURATION") }}
            </button>
          </div>
        </template>
        <template v-else-if="details.is_apple_silicon === 'no'">
          <div class="buttons buttons_sa">
            <button
              class="buttons__delete"
              @click.prevent="reset('send_free_provisions')"
            >
              <loader v-if="pending" size="15px" border="3px" absolute />
              {{ $t("SEND_FREE_PROVISIONS_TO_DEVICE") }}
            </button>
            <button
              class="buttons__delete"
              @click.prevent="reset('clear_free_developer_entity')"
            >
              <loader v-if="pending" size="15px" border="3px" absolute />
              {{ $t("CLEAR_RICKPACTOR_CONFIGURATION") }}
            </button>
          </div>
        </template>

        <h2 class="heading">{{ $t("GENERAL DEVICE CONFIGURATION") }}</h2>
        <div class="feature" v-for="(label, feature) in basic" :key="feature">
          <p class="feature__text g-text" v-html="label"></p>
          <yes-no class="feature__select" v-model="config[feature]" />
        </div>
        <p class="warning">
          {{ $t("ADVANCED_USAGE_ZONE_NOTICE") }}
        </p>
        <div
          class="feature"
          v-for="(label, feature) in advanced"
          :key="feature"
        >
          <p class="feature__text g-text" v-html="label"></p>
          <yes-no class="feature__select" v-model="config[feature]" />
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t("IDENTITY_TYPE_TO_USE_FOR_PRO") }}
          </p>
          <yes-no
            class="feature__select"
            :options="{
              auto: $t('AUTO'),
              development: $t('DEVELOPMENT'),
              production: $t('PRODUCTION'),
            }"
            v-model="config.signing_identity_type"
          />
        </div>

        <h2
          class="heading"
          v-html="
            $t('CONFIGURE_DEV_CERTIFICATE', {
              learnMoreLink:
                'https://forum.appdb.to/index.php?/topic/4707-tut-how-to-install-any-app-from-appdb-absolutely-for-free/',
            })
          "
        ></h2>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t("DEV_CERTIFICATE_BUNDLE") }}
            <span class="feature__info" v-if="details.p12">{{
              $t("ATTACHED")
            }}</span>
          </p>
          <file v-model="config.p12" class="feature__file" />
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t("DEV_CERTIFICATE_BUNDLE_PASSWORD") }}
          </p>
          <input
            v-model="config.p12_password"
            placeholder="Enter password"
            class="feature__input"
            type="text"
          />
        </div>
        <div class="feature">
          <p class="feature__text g-text">
            {{ $t("MOBILE_PROVISIONING_PROFILE") }}
            <span class="feature__info" v-if="details.provision">{{
              $t("ATTACHED")
            }}</span>
          </p>
          <file v-model="config.provision" class="feature__file" />
        </div>
        <div class="buttons">
          <button
            class="buttons__delete"
            @click.prevent="reset('clear_developer_entity')"
          >
            <loader v-if="pending" size="15px" border="3px" absolute />
            {{ $t("CLEAR_DEVELOPER_IDENTITY") }}
          </button>
          <button class="buttons__save" @click.prevent="save">
            <loader v-if="pending" size="15px" border="3px" absolute />
            {{ $t("SAVE_FEATURE_CONFIGURATION") }}
          </button>
        </div>
      </form>
      <html-with-links
        class="desc g-text"
        :html="
          '<p>' +
          $t('CONFIGURE_SUMMARY', {
            buyLink: '/my/buy',
            privacyLink: '/info/privacy',
          }) +
          '</p>'
        "
      ></html-with-links>
    </div>
  </section>
</template>

<script>
/* eslint-disable no-prototype-builtins */

import YesNo from '@/components/YesNo';
import Loader from '@/components/Loader';
import File from '@/components/Base64File';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import { isIDevice } from '@/utils/isItselfLinkable.js';

export default {
  components: {
    YesNo,
    Loader,
    File,
    HtmlWithLinks,
  },
  data() {
    return {
      inited: false,
      pending: false,
      config: {
        free_appleid: '',
        free_appleid_password: '',
        appsync: 'no',
        ignore_compatibility: 'no',
        ask_for_installation_options: 'no',
        enable_psychic_paper: 'no',
        disable_protection_checks: 'no',
        enable_inapp_patch: 'no',
        enable_trainer: 'no',
        is_pro_disabled: 'no',
        p12: null,
        p12_password: null,
        provision: null,
        model: '',
        signing_identity_type: 'auto',
        ios_version: '',
        is_opted_out_from_emails: 'no',
        is_automatic_linking_forbidden: 'no',
      },
      details: {
        is_apple_silicon: '',
        is_pro: '',
        ios_version: '',
        free_last_error: null,
        free_appleid: '',
        free_appleid_password: '',
        p12: '',
        provision: '',
      },
    };
  },
  computed: {
    title() {
      return this.$t('CONFIGURE_FEATURES');
    },
    majorIOSVersion() {
      if (!this.config.ios_version) {
        return 0;
      }

      return +this.config.ios_version.split('.')[0];
    },
    basic() {
      return {
        appsync: this.$t('IS_JAILBROKEN_WITH_APPSYNC', {
          appsyncLink: 'https://cydia.akemi.ai/',
        }),
        ignore_compatibility: this.$t('FORCE_APP_INSTALLATION'),
        ask_for_installation_options: this.$t('ASK_FOR_CUSTOM_INSTALLATION'),
        is_opted_out_from_emails: this.$t('OPT_OUT_DESCRIPTION'),
      };
    },
    advanced() {
      return {
        disable_protection_checks: this.$t('DISABLE_PROTECTION_CHECKS_DESC'),
        is_pro_disabled: this.$t('IS_PRO_DISABLED_DESC'),
        is_automatic_linking_forbidden: this.$t('FORBID_AUTOMATIC_LINKING'),
      };
    },
  },
  methods: {
    save() {
      if (this.pending) {
        return;
      }

      this.pending = true;
      this.$api.saveConfiguration(this.config)
        .then((response) => {
          this.checkInstallProvisionsURL(response);

          this.$notify({
            type: 'success',
            text: this.$t('FEATURE_CONFIGURATION_SAVED'),
          });
        })
        .then(this.getConfig)
        .then(() => new Promise((resolve) => {
          setTimeout(() => {
            this.$store.dispatch('getDevices')
              .finally(resolve);
          }, 300);
        }))
        .finally(() => {
          this.pending = false;
        });
    },
    checkInstallProvisionsURL(response) {
      if (response.install_provisions_via_rickpactor_url) {
        this.$alert(this.$t('IOS15_RICKPACTOR_MANUAL_FIX', { fixLink: response.install_provisions_via_rickpactor_url }));
      }
    },
    reset(param) {
      if (this.pending) {
        return;
      }

      if (param === 'send_free_provisions' && isIDevice() && this.majorIOSVersion >= 15) {
        this.$alert(this.$t('IOS15_MANUAL_FIX'));
        return;
      }

      this.pending = true;
      this.$api.saveConfiguration({ [param]: 'yes' })
        .then(this.checkInstallProvisionsURL)
        .then(this.getConfig)
        .finally(() => {
          this.pending = false;
        });
    },
    getConfig() {
      return this.$store.dispatch('getConfig')
        .then((config) => {
          Object.keys(config)
            .forEach((feature) => {
              if (this.config.hasOwnProperty(feature) && this.config[feature] !== null) {
                this.config[feature] = config[feature];
              }
              if (this.details.hasOwnProperty(feature)) {
                this.details[feature] = config[feature];
              }
            });
          this.inited = true;
        });
    },
  },
  created() {
    this.getConfig();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;
}

.feature {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e5e5;

  &__info {
    display: block;
    font-size: 12px;
  }

  &__text {
    margin-right: 60px;
  }

  &__select,
  &__file {
    margin-left: auto;
  }

  &__input {
    background: #f2f2f2;
    border: 1px solid #eeeeee;
    border-radius: 2px;
    padding: 6px;
    margin-left: auto;
    flex: 0 0 150px;
    width: 150px;
    font-size: 14px;
    font-family: "IBM Plex Sans", sans-serif;
    color: $text-primary;

    &::placeholder {
      font-family: "IBM Plex Sans", sans-serif;
      color: $text-primary;
    }
  }
}

.warning {
  color: #340b15;
  background: #fddee6;
  border-radius: 2px;
  padding: 3px 10px;
  text-align: center;
  line-height: 18px;
  margin: 20px 0;

  @include notebook {
    margin: 10px 0;
  }
}

.heading {
  color: #181f25;
  font-weight: 600;
  margin: 25px 0 15px;

  &:first-child {
    margin-top: 0;
  }

  @include notebook {
    margin: 15px 0 10px;
    line-height: 16px;
  }
}

.buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;

  @include tablet {
    flex-direction: column;
  }

  &_sa {
    justify-content: space-around;
  }

  &__delete,
  &__save {
    position: relative;
    padding: 5px 15px;
    font-size: 14px;
    border-radius: 2px;

    @include notebook {
      padding: 5px 8px;
    }

    @include tablet {
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }
  }

  &__delete {
    border: 1px solid rgba(250, 101, 101, 0.5);
  }

  &__save {
    background: $primary;
    color: #fff;
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-direction: column-reverse;
  }
}

.features,
.desc {
  @include notebook {
    width: 100%;
  }
}

.desc {
  background: #fbfbfb;
  border-radius: 8px;
  flex: 0 0 200px;
  margin-left: 40px;
  padding: 10px;

  @include notebook {
    margin: 0 0 15px;
    flex: 0 0 auto;
  }

  &::v-deep {
    p:not(:last-child) {
      margin-bottom: 15px;
    }
  }
}
</style>
